import { infiniteQueryFetchNextPage } from '@repo/sdk'
import type {
	OrganizationPerson,
	V1OrganizationsListPeopleData
} from '@repo/sdk/gen'
import { zV1OrganizationsListPeopleData } from '@repo/sdk/zod'
import { ListInputSearch } from '@repo/ui/components/list-input-search'
import { ListToolbar } from '@repo/ui/components/list-toolbar'
import { PageLayout } from '@repo/ui/components/page-layout'
import { QueryBreadcrumbs } from '@repo/ui/components/query-breadcrumbs'
import { getErrorCode, raise, zodNumberMaxValue } from '@repo/ui/utils'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { InvitePersonAction } from '@/routes/(private)/(shell)/admin/people/-components/invite-person-action'
import { PeopleEmpty } from '@/routes/(private)/(shell)/admin/people/-components/people-empty'
import { PeopleFilters } from '@/routes/(private)/(shell)/admin/people/-components/people-filters'
import { PeopleTable } from '@/routes/(private)/(shell)/admin/people/-components/people-table'
import { StoragePoolButton } from '@/routes/(private)/(shell)/admin/people/-components/storage-pool-button'
import {
	PERSON_ROLES,
	PERSON_STATUSES
} from '@/routes/(private)/(shell)/admin/people/-lib/person-status'
import { organizationPeopleQueryOptions } from '@/routes/(private)/(shell)/admin/people/-query/query-options'
import { usePageSize } from '@/stores/use-page-size'

const searchSchema = z.object({
	status: z.array(z.enum(PERSON_STATUSES)).optional(),
	role: z.array(z.enum(PERSON_ROLES)).optional(),
	search: z.string().trim().default('')
})

export const Route = createFileRoute('/(private)/(shell)/admin/people/')({
	component: AdminPeople,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	},
	staticData: {
		breadcrumb: 'admin-people:page_title'
	}
})

/**
 * Who is on the team: the people of the administrator's own organization and
 * the invitations still waiting, in one list.
 *
 * **ONE LIST, NOT TWO TABS.** Somebody administering thinks "who is on my
 * team", not "who is a user and who is an invitation". The status column is
 * what tells them apart, and the endpoint already answers with both kinds in
 * one page — splitting them here would invent a distinction the data does not
 * make and would leave the two halves paginating separately.
 *
 * **THE PERIMETER IS THE DATABASE'S.** `user`, `organization_membership` and
 * `organization_invite` all carry the row-level isolation policy and the read
 * runs inside the caller's organization context, so this screen does not
 * filter by organization and offers no way to ask for another one. Do not add
 * one: a client-side scope would be a second, weaker perimeter that a later
 * reader could mistake for the real one.
 *
 * **THE STATUS IS THE BACKEND'S READING, RENDERED AND NOT RE-DERIVED.** Two
 * cases depend on that: a ban whose deadline has passed reads `active` again,
 * and an invitation that has run out of time still reads `invited` — expiry is
 * a date beside the status, not a fifth status. The invitation column is where
 * that becomes visible, which is most of what this screen is opened for.
 *
 * Paging is the endpoint's cursor, consumed as an infinite query: the API hands
 * out `nextCursor` and no way to address a page by number. A cursor the server
 * rejects is answered by restarting from the first page in silence — showing an
 * error there would report a routine event as a fault.
 */
function AdminPeople() {
	const { t } = useTranslation()
	const params = Route.useSearch()
	const queryClient = useQueryClient()
	const pageSize = usePageSize((state) => state.pageSize)

	const listQuery = {
		limit: Math.min(pageSize, MAX_PAGE_SIZE),
		search: params.search || undefined,
		status: params.status,
		role: params.role
	} satisfies Omit<
		NonNullable<V1OrganizationsListPeopleData['query']>,
		'cursor'
	>

	const people = useInfiniteQuery(organizationPeopleQueryOptions(listQuery))
	const rows = people.data?.people ?? EMPTY_PEOPLE

	// Not an error to show. The cursor expired or the listing moved under it,
	// and the answer is another first page — which is what the load-more
	// handler asks the cache for.
	const cursorExpired = getErrorCode(people.error) === 'cursor_invalid'

	/**
	 * One more page, and the one refusal that is not a failure.
	 *
	 * `cursor_invalid` says the cursor no longer addresses anything — it
	 * expired, or the listing it was minted under has moved. The answer is to
	 * start again from the first page, silently: resetting the query drops the
	 * pages held and refetches with no cursor at all. An error toast here
	 * would report a routine event as a fault.
	 */
	async function loadNextPage() {
		const result = await infiniteQueryFetchNextPage(people)
		if (getErrorCode(result?.error) === 'cursor_invalid') {
			await queryClient.resetQueries({
				queryKey: makeQueryKeys.people.index(listQuery)
			})
		}
	}

	return (
		<PageLayout
			bar={
				// The pool sits beside «Invita persona» from `md` up; on a phone
				// that row is the search's, so it gets a row of its own above.
				<div className='flex flex-col gap-2'>
					<StoragePoolButton className='w-full md:hidden' />
					<ListToolbar
						actions={
							<>
								<div className='hidden md:contents'>
									<StoragePoolButton />
								</div>
								<InvitePersonAction />
							</>
						}
						search={
							<ListInputSearch
								placeholder={t(
									'admin-people:search_placeholder'
								)}
							/>
						}
					>
						<PeopleFilters />
					</ListToolbar>
				</div>
			}
			breadcrumb={<QueryBreadcrumbs />}
		>
			<div className='flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-6 md:px-6'>
				<PeopleTable
					data={rows}
					loadMore={{
						hasMore: people.hasNextPage,
						loading: people.isFetchingNextPage,
						onLoadMore: loadNextPage
					}}
					state={{
						empty: (
							<PeopleEmpty
								filtered={
									params.search !== '' ||
									params.status !== undefined
								}
							/>
						),
						error: people.isError && !cursorExpired,
						loading: people.isPending || people.isPlaceholderData
					}}
					total={people.data?.total ?? 0}
				/>
			</div>
		</PageLayout>
	)
}

const EMPTY_PEOPLE: OrganizationPerson[] = []

/**
 * The endpoint's own ceiling on a page, off the contract rather than retyped.
 * The rows-per-page preference is app-wide and its options are not bound to
 * this route's endpoint, so the two are reconciled here instead of by hoping
 * they stay in step.
 *
 * `raise` rather than a fallback: a schema that has lost its ceiling means the
 * contract changed shape, and a quietly substituted number would let this list
 * ask for pages the server refuses with a `400`.
 */
const MAX_PAGE_SIZE =
	zodNumberMaxValue(
		zV1OrganizationsListPeopleData.shape.query.unwrap().shape.limit
	) ?? raise('list-people lost its page-size ceiling')
